import React, { useState } from 'react';
import { Button } from '@mui/material';
import { Check, Copy } from 'lucide-react';
import { useI18n } from '../../../i18n/I18nProvider';
import ErrorLayout from './ErrorLayout';

/**
 * The 500 / unexpected error (spec 4.11): the 404's layout with the error code in a key
 * chip and a copy button that copies the code and the time, for whoever reports it.
 * The page error boundary (src/shared/ui/feedback/ErrorBoundary, in AppRoutes) renders it.
 */
export default function ServerErrorPage({ code, onRetry }: { code?: string | null; onRetry?: () => void }) {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);
  const shown = code || t('errorPages.serverNoCode');

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(`${shown} · ${new Date().toISOString()}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // No clipboard (an insecure origin or a blocked permission): the code stays on screen to copy by hand.
    }
  };

  return (
    <ErrorLayout
      code="500"
      eyebrow={t('errorPages.serverEyebrow')}
      title={t('errorPages.serverLead')}
      continuation={code ? (
        <>{t('errorPages.serverLeadMuted')} <kbd className="errp__code-chip">{code}</kbd>.</>
      ) : t('errorPages.serverLeadNoCode')}
      actions={(
        <>
          <Button variant="contained" onClick={onRetry ?? (() => window.location.reload())}>{t('errorPages.tryAgain')}</Button>
          <Button variant="outlined" href="/dashboard">{t('edDesign.notFound.home')}</Button>
          {code && (
            <Button variant="text" onClick={copy} startIcon={copied ? <Check size={16} aria-hidden="true" /> : <Copy size={16} aria-hidden="true" />}>
              {copied ? t('errorPages.copied') : t('errorPages.copyCode')}
            </Button>
          )}
          <span className="errp__sr" role="status">{copied ? t('errorPages.copied') : ''}</span>
        </>
      )}
    />
  );
}
